import { useState } from 'react';
import { CiGlobe } from 'react-icons/ci';
import { LuSparkles } from 'react-icons/lu';
import SelectTemplate from './SelectTemplate';
import { useNavigate } from 'react-router-dom';
import SelectAgentForm from './SelectAgentForm';
import { Typography, Box, useTheme, Theme } from '@mui/material';
import BuildScratchForm from './BuildScratchForm';
import { MdOutlineChevronLeft } from 'react-icons/md';
import { FaWandMagicSparkles } from 'react-icons/fa6';

type AgentOptionId = 'template' | 'quick_setup' | 'build_scratch';

export default function SelectAgent() {
  const theme = useTheme();
  const navigate = useNavigate();
  const [actionType, setActionType] = useState<AgentOptionId>('template');

  const renderTabComponent = (type: AgentOptionId) => {
    switch (type) {
      case 'template':
        return <SelectTemplate />;
      case 'quick_setup':
        return <SelectAgentForm />;
      case 'build_scratch':
        return <BuildScratchForm />;
      default:
        return null;
    }
  };

  return (
    <Box component='form'>
      <Box mx={4} px={4} py={2}>
        <Box display='flex'>
          <Box mt={0.3}>
            <MdOutlineChevronLeft size={24} cursor='pointer' onClick={() => navigate(-1)} />
          </Box>
          <Typography variant='h2' fontWeight={600} mb={2}>
            Bring your agent to life in one action
          </Typography>
        </Box>

        <Box display='flex' justifyContent='space-around' gap={5}>
          {agentOptions.map((agent) => (
            <AgentOptionCard
              key={agent.id}
              theme={theme}
              agent={agent}
              isSelected={actionType === agent.id}
              onSelect={setActionType}
            />
          ))}
        </Box>

        <Box mt={4}>{renderTabComponent(actionType)}</Box>
      </Box>
    </Box>
  );
}

interface AgentOption {
  id: AgentOptionId;
  name: string;
  description: string;
  icon: JSX.Element;
  style: {
    bg: string;
  };
}

const agentOptions: AgentOption[] = [
  {
    id: 'template',
    name: 'Choose from Template',
    description: 'Start with a pre-configured template for your specific needs.',
    icon: <LuSparkles size={28} color='#9F5FFF' />,
    style: { bg: '#F3E8FF' }
  },
  {
    id: 'quick_setup',
    name: 'Quick Assistant Setup',
    description: 'Quickly set up a new assistant with basic configuration.',
    icon: <CiGlobe size={28} color='#10B981' />,
    style: { bg: '#D1FAE5' }
  },
  {
    id: 'build_scratch',
    name: 'Build from Scratch',
    description: 'Create a fully customized assistant from the ground up.',
    icon: <FaWandMagicSparkles size={28} color='#3B82F6' />,
    style: { bg: '#DBEAFE' }
  }
];

interface AgentOptionCardProps {
  agent: AgentOption;
  theme: Theme;
  isSelected: boolean;
  onSelect: (id: AgentOptionId) => void;
}

const AgentOptionCard = ({ theme, agent, isSelected, onSelect }: AgentOptionCardProps) => (
  <Box
    onClick={() => onSelect(agent.id)}
    sx={{
      width: '100%',
      p: 2,
      border: '2px solid',
      borderRadius: 3,
      backgroundColor: '#fff',
      borderColor: isSelected ? theme.palette.primary.main : '#E5E5E5',
      cursor: 'pointer',
      textAlign: 'center',
      transition: 'all 0.2s ease-in-out',
      '&:hover': { borderColor: theme.palette.primary.main }
    }}
    role='button'
    tabIndex={0}
    onKeyPress={(e) => {
      if (e.key === 'Enter' || e.key === ' ') onSelect(agent.id);
    }}
  >
    <Box
      sx={{
        width: 56,
        height: 56,
        borderRadius: '50%',
        backgroundColor: agent.style.bg,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        mx: 'auto',
        mb: 2
      }}
    >
      {agent.icon}
    </Box>
    <Typography variant='h6' fontWeight='bold'>
      {agent.name}
    </Typography>
    <Typography
      variant='body2'
      color='text.secondary'
      sx={{
        mt: 1,
        display: '-webkit-box',
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        WebkitLineClamp: 2,
        lineHeight: '1.5em',
        minHeight: '3em'
      }}
    >
      {agent.description}
    </Typography>
  </Box>
);
